import React from 'react'
import { Button } from 'components/Form'

const DuplicateCustomerModal = ({ 
    isOpen, 
    onClose, 
    duplicateCustomer, 
    onCreateNew, 
    onLinkExisting, 
    loading = false,
    newCustomerData = {} // New prop to show what data will be used
}) => {
    if (!isOpen || !duplicateCustomer) return null

    const handleCreateNew = () => {
        onCreateNew(duplicateCustomer)
    }

    const handleLinkExisting = () => {
        onLinkExisting(duplicateCustomer)
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-screen overflow-y-auto dark:bg-boxdark">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-strokedark">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Duplicate Customer Detected
                    </h3>
                </div>

                {/* Body */}
                <div className="px-6 py-4">
                    <div className="mb-4">
                        <div className="flex items-center mb-3">
                            <span className="text-yellow-600 mr-2 text-xl">⚠️</span>
                            <p className="text-gray-700 dark:text-gray-300">
                                A customer with similar information already exists in the system.
                            </p>
                        </div>
                    </div>

                    {/* Existing Customer Information */}
                    <div className="mb-6 p-4 bg-gray-50 border rounded-lg dark:bg-gray-800 dark:border-strokedark">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                            Existing Customer:
                        </h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="font-medium text-gray-600 dark:text-gray-400">Name:</span>
                                <p className="text-gray-900 dark:text-white">
                                    {duplicateCustomer.first_name} {duplicateCustomer.last_name}
                                </p>
                            </div>
                            <div>
                                <span className="font-medium text-gray-600 dark:text-gray-400">Email:</span>
                                <p className="text-gray-900 dark:text-white">
                                    {duplicateCustomer.email || (
                                        <span className="text-yellow-600 italic">Not provided</span>
                                    )}
                                </p>
                            </div>
                            <div>
                                <span className="font-medium text-gray-600 dark:text-gray-400">Phone:</span>
                                <p className="text-gray-900 dark:text-white">
                                    {duplicateCustomer.phone_number || duplicateCustomer.mobile || (
                                        <span className="text-gray-500 italic">Not provided</span>
                                    )}
                                </p>
                            </div>
                            <div>
                                <span className="font-medium text-gray-600 dark:text-gray-400">Registration Status:</span>
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    duplicateCustomer.registration_status === 'complete' 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                    {duplicateCustomer.registration_status === 'complete' ? '✓' : '⚠️'} 
                                    {' '}{duplicateCustomer.registration_status?.charAt(0).toUpperCase() + duplicateCustomer.registration_status?.slice(1)}
                                </span>
                            </div>
                            {duplicateCustomer.date_joined && (
                                <div className="md:col-span-2">
                                    <span className="font-medium text-gray-600 dark:text-gray-400">Registered:</span>
                                    <p className="text-gray-900 dark:text-white">
                                        {new Date(duplicateCustomer.date_joined).toLocaleDateString('en-AU', {
                                            year: 'numeric',
                                            month: 'long', 
                                            day: 'numeric'
                                        })}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Data Comparison Section */}
                    {newCustomerData && Object.keys(newCustomerData).length > 0 && (
                        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900/20 dark:border-yellow-800">
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                                <span className="text-yellow-600 mr-2">⚠️</span>
                                Data Changes Preview
                            </h4>
                            <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-3">
                                If you choose to "Link to Existing Customer", the following information will be updated:
                            </p>
                            
                            <div className="space-y-3 text-sm">
                                {/* Name comparison */}
                                {(newCustomerData.user_first_name !== duplicateCustomer.first_name || 
                                  newCustomerData.user_last_name !== duplicateCustomer.last_name) && (
                                    <div className="grid grid-cols-2 gap-4 p-3 bg-white rounded border dark:bg-gray-800">
                                        <div>
                                            <span className="font-medium text-gray-600 dark:text-gray-400">Current Name:</span>
                                            <p className="text-gray-900 dark:text-white">
                                                {duplicateCustomer.first_name} {duplicateCustomer.last_name}
                                            </p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-green-600 dark:text-green-400">Will become:</span>
                                            <p className="text-green-800 dark:text-green-200 font-medium">
                                                {newCustomerData.user_first_name} {newCustomerData.user_last_name}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Email comparison */}
                                {newCustomerData.user_email && newCustomerData.user_email !== duplicateCustomer.email && (
                                    <div className="grid grid-cols-2 gap-4 p-3 bg-white rounded border dark:bg-gray-800">
                                        <div>
                                            <span className="font-medium text-gray-600 dark:text-gray-400">Current Email:</span>
                                            <p className="text-gray-900 dark:text-white">
                                                {duplicateCustomer.email || <span className="text-gray-500 italic">Not provided</span>}
                                            </p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-green-600 dark:text-green-400">Will become:</span>
                                            <p className="text-green-800 dark:text-green-200 font-medium">
                                                {newCustomerData.user_email}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Phone comparison */}
                                {newCustomerData.user_phone_number && 
                                 newCustomerData.user_phone_number !== (duplicateCustomer.phone_number || duplicateCustomer.mobile) && (
                                    <div className="grid grid-cols-2 gap-4 p-3 bg-white rounded border dark:bg-gray-800">
                                        <div>
                                            <span className="font-medium text-gray-600 dark:text-gray-400">Current Phone:</span>
                                            <p className="text-gray-900 dark:text-white">
                                                {duplicateCustomer.phone_number || duplicateCustomer.mobile || 
                                                 <span className="text-gray-500 italic">Not provided</span>}
                                            </p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-green-600 dark:text-green-400">Will become:</span>
                                            <p className="text-green-800 dark:text-green-200 font-medium">
                                                {newCustomerData.user_phone_number}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Options */}
                    <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                            What would you like to do?
                        </h4>
                        
                        <div className="space-y-3">
                            <div className="p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:border-strokedark">
                                <div className="flex items-start">
                                    <div className="flex-1">
                                        <h5 className="font-medium text-gray-900 dark:text-white">
                                            Link to Existing Customer
                                        </h5>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                            Use the existing customer record for this product registration.
                                            This is recommended if it's the same person.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:border-strokedark">
                                <div className="flex items-start">
                                    <div className="flex-1">
                                        <h5 className="font-medium text-gray-900 dark:text-white">
                                            Create New Customer
                                        </h5>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                            Create a separate customer record. Use this if they are different people
                                            (e.g., family members with same contact details).
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-strokedark">
                    <div className="flex items-center justify-end gap-3">
                        <Button
                            type="button"
                            className="px-4 py-2 text-gray-600 border-gray-300 hover:bg-gray-50 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-800"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            className="px-4 py-2 text-white bg-blue-600 border-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:border-blue-600"
                            onClick={handleCreateNew}
                            disabled={loading}
                        >
                            {loading ? 'Creating...' : 'Create New Customer'}
                        </Button>
                        <Button
                            type="button"
                            className="px-4 py-2 text-white bg-primary border-primary hover:bg-primary/90 dark:bg-secondary dark:border-secondary"
                            onClick={handleLinkExisting}
                            disabled={loading}
                        >
                            {loading ? 'Linking...' : 'Link to Existing'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default DuplicateCustomerModal
